import type { Survey, SurveyOverallScores, SurveyScoresByCategory } from '@engagement-web-app/common';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, type ErrorComponentProps, stripSearchParams } from '@tanstack/react-router';
import { useState } from 'react';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Skeleton } from '@/components/Skeleton';
import {
    SurveyCategoriesComparisonChart,
    SurveyCategoriesComparisonChartSkeleton,
} from '@/components/surveys/categories/SurveyCategoriesComparisonChart';
import {
    SurveyCategoriesComparisonTable,
    SurveyCategoriesComparisonTableSkeleton,
} from '@/components/surveys/categories/SurveyCategoriesComparisonTable';
import {
    SurveyCategoryXAttributesComparisonChart,
    SurveyCategoryXAttributesComparisonChartSkeleton,
} from '@/components/surveys/categories/SurveyCategoryXAttributesComparisonChart';
import { CreatePdfButton } from '@/components/surveys/common/CreatePdfButton';
import { Ranking, RankingSkeleton } from '@/components/surveys/common/Ranking';
import { fetchScoresByCategory, fetchSurveys } from '@/queries/surveys/system';
import { hiddenInPrint, pageBreakBefore, pageBreakBeforeInLandscape } from '@/styles/print.css';
import {
    useOverallScores,
    useScoresForAttributeComparisonChart,
    useScoresForCategoriesComparisonTable,
    useSurveyAttributes,
} from '../-hooks';
import { DefaultRouteQueryParams, validateSearch } from '../-search';

const ERROR_MESSAGE = 'カテゴリー分析の取得に失敗しました。'; // also used below for the in-page query error

export const Route = createFileRoute('/system/_auth/surveys/categories/')({
    loaderDeps({ search: { organizationId, surveyId } }) {
        return { organizationId, surveyId };
    },
    loader: async ({ deps }) => {
        return fetchSurveys(deps.organizationId);
    },
    validateSearch,
    search: {
        middlewares: [stripSearchParams(DefaultRouteQueryParams)],
    },
    component: SurveyCategoriesRoute,
    pendingComponent: ContentSkeleton,
    errorComponent: SurveyCategoriesTabError,
});

function SurveyCategoriesTabError({ reset }: ErrorComponentProps) {
    return <ErrorOverlay active onRetry={reset} message={ERROR_MESSAGE} />;
}

export function SurveyCategoriesRoute() {
    const { organizationId, surveyId: selectedSurveyId } = Route.useSearch();
    const { surveys } = Route.useLoaderData();

    const currentSurveyIdx = surveys.findIndex((s) => s.surveyId === selectedSurveyId);
    const surveyIdsToCompare = surveys.slice(currentSurveyIdx).map((s) => s.surveyId);

    const scoresByCategoryQuery = useQuery({
        queryKey: ['scores-by-category', organizationId, selectedSurveyId],
        queryFn: () => fetchScoresByCategory(organizationId, { surveyIds: surveyIdsToCompare }),
        enabled: surveyIdsToCompare.length > 0,
    });

    const overallScoresQuery = useOverallScores(organizationId, surveyIdsToCompare);
    const selectedSurveyOverallScores = overallScoresQuery.data?.scores.find((s) => s.surveyId === selectedSurveyId);

    const selectedSurvey = surveys.find((s) => s.surveyId === selectedSurveyId);

    return (
        <>
            {scoresByCategoryQuery.isPending && <ContentSkeleton />}

            <ErrorOverlay
                active={scoresByCategoryQuery.isError}
                onRetry={scoresByCategoryQuery.refetch}
                message={ERROR_MESSAGE}
            />

            {scoresByCategoryQuery.data && selectedSurvey && (
                <Content
                    organizationId={organizationId}
                    scores={scoresByCategoryQuery.data.scores}
                    selectedSurvey={selectedSurvey}
                    isError={scoresByCategoryQuery.isError}
                    onRetry={scoresByCategoryQuery.refetch}
                    companyAverage={
                        overallScoresQuery.data
                            ? {
                                  di: selectedSurveyOverallScores?.di ?? null,
                                  deviation: selectedSurveyOverallScores?.deviation ?? null,
                              }
                            : null
                    }
                    isCompanyAverageError={overallScoresQuery.isError}
                    onCompanyAverageRetry={overallScoresQuery.refetch}
                />
            )}
        </>
    );
}

type ContentProps = {
    organizationId: string;
    scores: SurveyScoresByCategory[];
    selectedSurvey: Survey;
    isError: boolean;
    onRetry: () => void;
    /** Company-wide scores of the selected survey; `null` while still loading. */
    companyAverage: Pick<SurveyOverallScores, 'di' | 'deviation'> | null;
    isCompanyAverageError: boolean;
    onCompanyAverageRetry: () => void;
};

function Content({
    organizationId,
    scores,
    selectedSurvey,
    isError,
    onRetry,
    companyAverage,
    isCompanyAverageError,
    onCompanyAverageRetry,
}: ContentProps) {
    const [attributesForComparisonTable, setAttributesForComparisonTable] = useState<string[]>([]);
    const [attributesForComparisonChart, setAttributesForComparisonChart] = useState<string[]>([]);

    const selectedSurveyId = selectedSurvey.surveyId;
    const previousSurveyId = selectedSurvey.previousSurveyId;

    const currentSurveyScores = scores.find((s) => s.surveyId === selectedSurveyId)!;

    const surveyAttributesQuery = useSurveyAttributes(organizationId, selectedSurveyId);

    const {
        scoresForCategoryComparisonTable,
        previousScoresForCategoryComparisonTable,
        isError: isCategoryComparisonTableError,
        retry: retryCategoryComparisonTable,
    } = useScoresForCategoriesComparisonTable(
        organizationId,
        selectedSurveyId,
        previousSurveyId ?? null,
        attributesForComparisonTable
    );

    const {
        scoresForAttributeComparisonChart,
        isError: isAttributeComparisonChartError,
        retry: retryAttributeComparisonChart,
    } = useScoresForAttributeComparisonChart(organizationId, selectedSurveyId, attributesForComparisonChart);

    return (
        <Flex direction="column" gap="5">
            <Flex justify="end" className={hiddenInPrint}>
                <CreatePdfButton />
            </Flex>

            <Flex gap="4">
                <Ranking
                    title="よかった​カテゴリー"
                    variant="positive"
                    items={currentSurveyScores.categories}
                    isError={isError}
                    onRetry={onRetry}
                />
                <Ranking
                    title="改善必要カテゴリー"
                    variant="negative"
                    items={currentSurveyScores.categories}
                    isError={isError}
                    onRetry={onRetry}
                />
            </Flex>

            {/* Print layout: rankings and the year-over-year chart, then the comparisons. */}
            <div className={pageBreakBeforeInLandscape}>
                <SurveyCategoriesComparisonChart
                    scores={scores}
                    companyAverage={companyAverage}
                    isError={isError || isCompanyAverageError}
                    onRetry={() => {
                        if (isError) onRetry();
                        if (isCompanyAverageError) onCompanyAverageRetry();
                    }}
                />
            </div>

            <div className={pageBreakBefore}>
                <SurveyCategoriesComparisonTable
                    scores={scoresForCategoryComparisonTable.data ?? null}
                    previousScores={previousScoresForCategoryComparisonTable.data ?? null}
                    attributeSettings={surveyAttributesQuery.data?.attributes ?? null}
                    selectedAttributeOptionIds={attributesForComparisonTable}
                    onSelectedAttributeOptionIdsChange={setAttributesForComparisonTable}
                    isFetching={
                        scoresForCategoryComparisonTable.isFetching ||
                        previousScoresForCategoryComparisonTable.isFetching
                    }
                    isError={isCategoryComparisonTableError}
                    onRetry={retryCategoryComparisonTable}
                />
            </div>

            <div className={pageBreakBefore}>
                <SurveyCategoryXAttributesComparisonChart
                    scores={scoresForAttributeComparisonChart.data ?? null}
                    attributeSettings={surveyAttributesQuery.data?.attributes ?? null}
                    selectedAttributeOptionIds={attributesForComparisonChart}
                    onSelectedAttributeOptionIdsChange={setAttributesForComparisonChart}
                    isFetching={scoresForAttributeComparisonChart.isFetching}
                    isError={isAttributeComparisonChartError}
                    onRetry={retryAttributeComparisonChart}
                />
            </div>
        </Flex>
    );
}

function ContentSkeleton() {
    return (
        <Flex direction="column" gap="5">
            <Flex justify="end">
                <Skeleton.Box width={168} height={36} />
            </Flex>

            <Flex gap="4">
                <RankingSkeleton />
                <RankingSkeleton />
            </Flex>

            <SurveyCategoriesComparisonChartSkeleton />

            <SurveyCategoriesComparisonTableSkeleton />

            <SurveyCategoryXAttributesComparisonChartSkeleton />
        </Flex>
    );
}
